import './AddFeed.css';
import React, { useState, useContext, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { FlowForm, getKeyHandler } from 't-lib-ui/structure';
import { TextButton, Spinner } from 't-lib-ui/widgets';
import { RouterContext, goTo, useParams, useLocation } from 't-lib-ui/router';
import ChevronLeftIcon from './ChevronLeftIcon';
import RotatingMessage from './RotatingMessage';
import FeedTile from './FeedTile';
import { useEscapeBack } from '../utils/useEscapeBack';
import { useGridColumns } from '../utils/useGridColumns';
import FeedPreview from './FeedPreview';
import CategoryTile from './CategoryTile';
import { CategoryIcon } from './CategoryIcons';
import { formatCategory } from '../utils/formatting';
import { AppState } from '../context/general';
import { nonNull } from 't-lib-ui/helpers';
import { requestFeed, subscribeWithMode, useGetCatalogCategories, useGetCatalogByCategory, useGetDiscoverOnboarding, useSearchCatalog, getPublicFeed, getPublicFeedByUrl } from '../connector/api';
import { RestUserFeed, RestCatalog, CachedFeedResult } from '../schema-copy/api-schema';
import { getPlanLimits } from '../schema-copy/userPlans';
import { CategoryWithSubcategories } from '../schema-copy/feed-schema';
import { QueryStatus } from 't-lib-ui';
import { FetchError } from 't-lib-ui/connector';
import { isSuperuser } from '../schema-copy/capabilities';
import { getDefaultIconUrl } from '../schema-copy/default-icon';
import FeedSearchBox from './FeedSearchBox';
import { isPrivateOnlyUrl, getRssTunnelDomain, isNewsGoogleUrl, isRedditUrl, REDDIT_UNSUPPORTED_MESSAGE } from '../schema-copy/feed-privacy';
import { checkFeedUrlSafety, feedUrlRejectionMessage } from '../schema-copy/feed-url-safety';
import { isYoutubeUrl, isUrlLike } from '../utils/url';
import { markFeedAsNew } from '../connector/post-engine';
import FeedQuotaDialog from './FeedQuotaDialog';
import FeedCreationLimitDialog from './FeedCreationLimitDialog';
import FeedQuotaNote from './FeedQuotaNote';
import { useFeedQuota } from '../connector/feed-quota';
import MailIcon from './MailIcon';
import DiscoverTile, { DiscoverTilePlaceholder } from './DiscoverTile';
import { requestStartCollapsed } from './ThreeColumnLayout';
import { isNativeApp } from '../utils/platform';
import { setPreviewReturnPath, setFeedOpenedFromTile, setFeedOpenedFromSearch, saveAddFeedReturn, takeAddFeedRestore, isAddFeedFromHome, setAddFeedFromHome, setTopicViaAllTopics, isTopicViaAllTopics, requestHomeScrollRestore } from '../pages/homeScroll';
import { ADD_FEED_LISTS, ONBOARDING_LISTS, AddFeedListId, catalogToHomeFeed, BrowseSectionHeader, CollectionTile, RecentFeedsList } from './AddFeedBrowse';

// Shown when a YouTube channel/video fails to add. YouTube's videos.xml feeds fail
// intermittently (rate limiting, flaky endpoints), so we never blacklist them
// (see sync-server writer.ts) and tell the user to try again later instead of
// showing the generic "feed not found / broken" error.
const YOUTUBE_UNRELIABLE_MESSAGE = 'YouTube feeds are unreliable and sometimes fail even for valid channels. Please try adding this again in a little while.';

// True for any YouTube page or share link (youtube.com / youtu.be).
const isYoutubeInput = (url: string | null | undefined): boolean => {
  if (!url) return false;
  if (isYoutubeUrl(url)) return true;
  try {
    return new URL(url).hostname.toLowerCase() == 'youtu.be';
  } catch {
    return false;
  }
};

// Rows of topics shown on the start page; the rest are behind "See all". The column count (4 or 5)
// follows the page width via container queries in AddFeedBrowse.css.
const START_TOPIC_ROWS = 2;

const buttonKeyHandler = getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any;

interface AddFeedProps {
  onFeedAdded: (feedId: string) => void;
  autoFocus?: boolean;
}

/** Parses the Add Feed path. Besides categories (/a/add/<category>[/<sub>]), it can name a feed
 *  (/a/add/feed/<id>), the list of all topics (/a/add/topics) or a collection (/a/add/list/<id>). */
export const useAddFeedPath = () => {
  const { category: categoryParam, subCategory } = useParams<{ category?: string; subCategory?: string }>();
  const isFeedView = categoryParam == 'feed';
  const isTopicsView = categoryParam == 'topics';
  const category = isFeedView || isTopicsView || categoryParam == 'list' ? undefined
    : categoryParam && subCategory ? `${categoryParam}/${subCategory}` : categoryParam;
  return {
    isFeedView,
    feedId: isFeedView ? subCategory : undefined,
    isTopicsView,
    listId: categoryParam == 'list' ? subCategory : undefined,
    // Hidden categories (adult, piracy) have no page: their feeds are only found by URL or domain.
    category: category && formatCategory(category) ? category : undefined,
  };
};

const AddFeed: React.FC<AddFeedProps> = ({ onFeedAdded, autoFocus }) => {
  const { isFeedView, feedId, isTopicsView, listId, category } = useAddFeedPath();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const isAdmin = isSuperuser(appState.state.authData?.selectedMembership?.capabilities);
  const canUseNewsletters = getPlanLimits(appState.state.currentUser?.userPlan ?? 'free').maxNewsletterFeeds > 0;
  const { atLimit: atFeedQuota } = useFeedQuota();
  const [topicGridRef, topicCols] = useGridColumns(4);
  // The search is kept in the URL (/a/add?q=<term>), so it survives a reload, the back button of a feed
  // opened from the results, and can be started from elsewhere (Home's search bar). A catalog search
  // from the URL is the initial state, so the search list query starts with it.
  const location = useLocation();
  const qParam = location.searchParams.get('q') ?? '';
  const initialQuery = qParam && !isFeedView && !isUrlLike(qParam) ? qParam : '';
  // The term of the last search, to tell our own URL updates from navigation; '' when no search is shown.
  const searchedTermRef = useRef(initialQuery);
  const [searchInput, setSearchInput] = useState(initialQuery);
  const [statusMessage, setStatusMessage] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [feedInfo, setFeedInfo] = useState<RestUserFeed | null>(null);
  const [searchActive, setSearchActive] = useState(!!initialQuery);
  // The start state: no search, feed, category, topic list or collection open.
  const startMode = !searchActive && !feedId && !category && !isTopicsView && !listId;
  const browsing = !searchActive && !feedId;
  // Topic pages entered from Home's Categories section lead back to Home until the start page is shown.
  useEffect(() => {
    if (!startMode) return;
    setAddFeedFromHome(false);
    setTopicViaAllTopics(false);
  }, [startMode]);
  // A topic page opened from "All topics" leads back there (see CategoryCrumbs).
  const openTopic = (cat: string, viaAllTopics: boolean) => {
    setTopicViaAllTopics(viaAllTopics);
    goTo(routerCtx, `/a/add/${cat}`);
  };
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [categories, setCategories] = useState<string[]>([]);
  const [searchedUrl, setSearchedUrl] = useState<string | null>(null);
  const [isStatusError, setIsStatusError] = useState(false);
  const [showQuotaDialog, setShowQuotaDialog] = useState(false);
  const [showCreationLimitDialog, setShowCreationLimitDialog] = useState(false);
  const [cachedFeed, setCachedFeed] = useState<CachedFeedResult | null>(null);
  const [isReadyAction, setIsReadyAction] = useState(false);

  const [subscribedFeedIds, setSubscribedFeedIds] = useState<Set<string>>(() => new Set(appState.state.currentUser?.feeds ?? []));
  const categoriesQuery = useGetCatalogCategories();

  useEffect(() => {
    const update = () => setSubscribedFeedIds(new Set(appState.feedEngine.getFeeds().map(f => f.id)));
    return appState.feedEngine.onFeedsChange(update);
  }, [appState.feedEngine]);

  const isSubscribed = useCallback((feedId: string) =>
    subscribedFeedIds.has(feedId) || appState.feedEngine.isRecentlySubscribed(feedId)
  , [subscribedFeedIds, appState.feedEngine]);

  const categoryFeeds = useGetCatalogByCategory(10, category, { enabled: !!category });
  // Feeds of all categories, most popular first: the start page's row and the "Most popular" list.
  const popularFeeds = useGetCatalogByCategory(10, undefined, { enabled: startMode || listId == 'popular' });
  // "Recently added", "Random" and "High frequency" come from the hourly onboarding file on the CDN.
  const onboardingList = listId && ONBOARDING_LISTS[listId as AddFeedListId];
  const onboarding = useGetDiscoverOnboarding({ enabled: !!onboardingList });
  const topLevelCategories = useMemo(() => categories.filter(cat => !cat.includes('/')), [categories]);
  const searchFeeds = useSearchCatalog(10, searchQuery, { enabled: searchActive && !!searchQuery });

  useEffect(() => {
    if (categoriesQuery.data) {
      // 'chat' is reserved for future applications and not offered for browsing yet
      setCategories((categoriesQuery.data.categories || []).filter(cat => cat != 'chat'));
    }
  }, [categoriesQuery.data]);

  useEffect(() => {
    if (category)
      categoryFeeds.changeInput({ category });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Load feed when feedId is present
  useEffect(() => {
    if (feedId) {
      const loadFeed = async () => {
        setIsSearching(true);
        setStatusMessage('Loading feed...');
        setFeedInfo(null);
        setSearchActive(true);

        try {
          const feedOrCatalog = await getPublicFeed(feedId, appState.state.authData);
          setFeedInfo(feedOrCatalog as RestUserFeed);
          setStatusMessage('');
        } catch (error) {
          console.error('Error loading feed:', error);
          setStatusMessage('Error loading feed: ' + (error as Error).message);
        } finally {
          setIsSearching(false);
        }
      };

      loadFeed();
    }
  }, [feedId, appState.state.authData]);

  // Navigate back to /a/add when user starts typing while viewing a feed
  useEffect(() => {
    if (feedId && searchInput.trim()) {
      goTo(routerCtx, '/a/add');
    }
  }, [feedId, searchInput, routerCtx]);

  // (Not while a search from the URL is starting: its input is set after this ran on mount.)
  useEffect(() => {
    if (!searchInput.trim() && !searchedTermRef.current) {
      setFeedInfo(null);
      setStatusMessage('');
      setIsStatusError(false);
      setIsSearching(false);
      setSearchActive(false);
      setSearchQuery('');
      setSearchedUrl(null);
      setCachedFeed(null);
    }
  }, [searchInput]);

  const normalizeUrl = (input: string): string => {
    let normalized = input.trim();

    // Remove trailing space if present (from case 3)
    normalized = normalized.trimEnd();

    // Add https:// if missing
    if (!normalized.startsWith('http:') && !normalized.startsWith('https:')) {
      normalized = 'https://' + normalized;
    }

    // Add trailing slash if no path. Must go on the path, not the end of the
    // string: 'https://www.welt.de/?service=Rss' would otherwise become
    // '...?service=Rss/' and no longer resolve to the feed.
    try {
      const searchInputObj = new URL(normalized);
      if (!searchInputObj.pathname || searchInputObj.pathname == '/') {
        searchInputObj.pathname = '/';
        normalized = searchInputObj.toString();
      }
    } catch {
      // If URL parsing fails, just add trailing slash if not present
      if (!normalized.endsWith('/') && !normalized.includes('/', 8) && !normalized.includes('?') && !normalized.includes('#')) {
        normalized += '/';
      }
    }

    return normalized;
  };

  const loadingMessages = [
    'Checking feed...',
    'Loading feed...',
    'Analyzing...',
    'Almost there...',
    'Reading more...',
    'Just a bit more...',
    'Wondering...',
    'Investigating...',
    'Please wait...',
    'This is taking longer than expected...',
    'Still working on it...',
    'Hang tight...',
  ];

  const looksLikeFeedUrl = (url: string | null | undefined): boolean => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('rss') || lower.includes('feed') || lower.includes('.xml');
  };

  const getStatusMessage = (status: string, errorType?: string, url?: string | null): string => {
    switch (status) {
      case 'FETCHING':
        return 'rotating'; // Special marker for rotating messages
      case 'BLOCK':
        return 'This feed content is not supported.';
      case 'BAD':
        if (isYoutubeInput(url ?? searchedUrl))
          return YOUTUBE_UNRELIABLE_MESSAGE;
        if (errorType == 'BLOCKED' && !looksLikeFeedUrl(url ?? searchedUrl))
          return 'This site blocks our bot from checking it. You can try to locate the RSS feed URL on the site (often linked from the footer or a "feeds" page) and paste it here directly.';
        if (errorType == 'BLOCKED' || errorType == 'HTTP')
          return 'Could not connect to this feed. The URL may be incorrect or the site may be temporarily unavailable.';
        else if (errorType == 'NO_RSS')
          return 'This URL does not appear to contain an RSS feed. Please check the URL or try the site\'s homepage.';
        else if (errorType == 'PARSE')
          return 'Found a feed but could not read it. The feed format may be invalid or corrupted.';
        else
          return 'Feed not found or broken. Please check the URL and try again later.';
      case 'READY':
        return 'Feed found! Choose how to follow it.';
      case 'ACTIVE':
      case 'PRIVATE':
        return 'Feed is ready!';
      case 'UNUSED':
        return 'This feed is no longer available.';
      default:
        return `Feed status: ${status}`;
    }
  };

  const pollForFeed = async (searchInput: string, maxAttempts: number = 12): Promise<RestUserFeed | null> => {
    const auth = nonNull(appState.state.authData);
    const waitTimes = [3000, 2000, 2000, 2000, 3000, 3000, 4000];

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        // Wait 5 seconds between polls
        await new Promise(resolve => setTimeout(resolve, waitTimes[attempt - 1] || 5000));
      }

      try {
        const result = await requestFeed(auth, searchInput);

        // Check if we got a full feed (has 'id' property)
        if ('id' in result) {
          // The feed was fetched moments ago, so its current.dz may still be on its way to the
          // CDN. Read it from the sync instance (with ?w) instead of 404-ing against the CDN.
          markFeedAsNew(result.id);
          return result as RestUserFeed;
        }

        // Check status and abort if needed
        const status = (result as any).feedStatus;
        const errorType = (result as any).errorType;
        if (status == 'READY') {
          setCachedFeed((result as any).cachedFeed);
          setStatusMessage(getStatusMessage(status, undefined, searchInput));
          return null;
        }
        if (status == 'BLOCK' || status == 'BAD') {
          setStatusMessage(getStatusMessage(status, errorType, searchInput));
          return null;
        }

        // Update status message for transitional states
        setStatusMessage(getStatusMessage(status, errorType, searchInput));
      } catch (error) {
        if (error instanceof FetchError && error.type == 'InvalidRequest') {
          setStatusMessage('We could not find an RSS or similar feed at this location.');
          setIsStatusError(true);
          return null;
        }
        console.error('Error polling feed:', error);
        setStatusMessage('Error checking feed status. Retrying...');
      }
    }

    return null;
  };

  // The server rejected a subscribe with 'AlreadySubscribed': the subscription exists (typically a
  // first attempt that looked like it failed but went through), so there is nothing left to create.
  // Adopt it locally and present it as a completed subscribe instead of an error.
  const finishAlreadySubscribed = async (subscribedFeedId?: string) => {
    if (!subscribedFeedId) {
      // No feed id to work with (older server) — at least don't call it an error.
      setCachedFeed(null);
      setStatusMessage('You are already following this feed.');
      setIsStatusError(false);
      return;
    }
    markFeedAsNew(subscribedFeedId);
    const feed = await appState.feedEngine.adoptExistingSubscription(subscribedFeedId);
    if (feed) setFeedInfo(feed);
    setCachedFeed(null);
    setStatusMessage(feed ? '' : 'You are already following this feed.');
    setIsStatusError(false);
    onFeedAdded(subscribedFeedId);
  };

  const handleSearch = async (inputOverride?: string) => {
    let input = inputOverride ?? searchInput;
    if (!input) return;

    // Auto-decode URL-encoded URLs (e.g. pasted from encoded contexts)
    const trimmed = input.trim();
    const trimmedLower = trimmed.toLowerCase();
    if (trimmedLower.startsWith('https%3a%2f%2f') || trimmedLower.startsWith('http%3a%2f%2f')) {
      input = decodeURIComponent(trimmed);
      setSearchInput(input);
    }

    setSearchActive(true);
    // Refining a search (or one from a ?url deep link) replaces it in the history; a new one becomes a page.
    searchedTermRef.current = input.trim();
    if (qParam != searchedTermRef.current)
      goTo(routerCtx, `/a/add?q=${encodeURIComponent(searchedTermRef.current)}`, qParam || location.searchParams.has('url') ? 'replace' : 'push');

    if (!isUrlLike(input)) {
      // Trigger catalog search
      setFeedInfo(null);
      setStatusMessage('');
      setIsSearching(false);
      const trimmedQuery = input.trim();
      setSearchQuery(trimmedQuery);
      searchFeeds.changeInput({ query: trimmedQuery });
      return;
    }

    // For URL-like inputs, proceed with feed request
    setSearchQuery('');

    // Refuse unsafe URLs (IP/localhost/.local hosts, non-standard ports, downread.com)
    // before hitting the server — the API enforces the same rules.
    const safetyRejection = checkFeedUrlSafety(normalizeUrl(input));
    if (safetyRejection) {
      setStatusMessage(feedUrlRejectionMessage(safetyRejection));
      setIsStatusError(true);
      setIsSearching(false);
      setFeedInfo(null);
      return;
    }

    // Reddit rate-limits RSS too aggressively for us to poll — only admins may still add it.
    if (isRedditUrl(normalizeUrl(input)) && !isAdmin) {
      setStatusMessage(REDDIT_UNSUPPORTED_MESSAGE);
      setIsStatusError(true);
      setIsSearching(false);
      setFeedInfo(null);
      return;
    }

    setIsSearching(true);
    setStatusMessage('Searching for feed...');
    setIsStatusError(false);
    setFeedInfo(null);
    setSearchedUrl(normalizeUrl(input));

    // Anonymous users can only follow feeds that already exist in our catalog — they can't create
    // new feeds by URL (that needs an account). Look the URL up; show it if it exists.
    if (!appState.state.authData) {
      try {
        const feed = await getPublicFeedByUrl(normalizeUrl(input));
        setFeedInfo(feed as RestUserFeed);
        setStatusMessage('');
      } catch {
        setStatusMessage("We don't have this feed yet. Sign in to add new feeds by URL.");
        setIsStatusError(true);
      } finally {
        setIsSearching(false);
      }
      return;
    }

    try {
      const normalizedUrl = normalizeUrl(input);
      const auth = nonNull(appState.state.authData);

      // Initial request
      let result;
      try {
        result = await requestFeed(auth, normalizedUrl);
      } catch (e) {
        // YouTube feeds fail intermittently; surface the "try again later" message rather
        // than the generic rejection/invalid-request text.
        if (e instanceof FetchError && (e.type == 'FeedRejected' || e.type == 'InvalidRequest') && isYoutubeInput(normalizedUrl)) {
          setStatusMessage(YOUTUBE_UNRELIABLE_MESSAGE);
          setIsStatusError(true);
          setIsSearching(false);
          return;
        }
        if (e instanceof FetchError && e.type == 'FeedRejected') {
          const reason = (e as any).extraData?.reason;
          if (reason == 'illegal')
            setStatusMessage('This feed contains illegal content and cannot be added.');
          else if (reason == 'no-rss')
            setStatusMessage('This URL does not appear to contain an RSS or Atom feed.');
          else if (reason == 'reddit')
            setStatusMessage(REDDIT_UNSUPPORTED_MESSAGE);
          else if (reason == 'private-only')
            setStatusMessage('This feed appears to be personal. Please add it as a private feed.');
          else
            setStatusMessage(e.message || 'This feed cannot be added.');
          setIsStatusError(true);
          setIsSearching(false);
          return;
        }
        if (e instanceof FetchError && e.type == 'InvalidRequest') {
          setStatusMessage(e.message || 'We could not find an RSS or similar feed at this location.');
          setIsStatusError(true);
          setIsSearching(false);
          return;
        }
        throw e;
      }

      // Check if we got a full feed immediately (ACTIVE or PRIVATE)
      if ('id' in result) {
        setFeedInfo(result as RestUserFeed);
        setStatusMessage('');
        setIsSearching(false);
        return;
      }

      // READY: show decision UI immediately
      const initialStatus = (result as any).feedStatus;
      if (initialStatus == 'READY') {
        setCachedFeed((result as any).cachedFeed);
        setStatusMessage(getStatusMessage('READY', undefined, normalizedUrl));
        setIsSearching(false);
        return;
      }

      // Otherwise, start polling
      const errorType = (result as any).errorType;
      setStatusMessage(getStatusMessage(initialStatus, errorType, normalizedUrl));

      const feed = await pollForFeed(normalizedUrl);

      if (feed) {
        setFeedInfo(feed);
        setStatusMessage('');
      } else if (!cachedFeed) {
        if (isYoutubeInput(normalizedUrl)) {
          setStatusMessage(YOUTUBE_UNRELIABLE_MESSAGE);
          setIsStatusError(true);
        } else {
          // Only show timeout message if we didn't transition to READY during polling
          setStatusMessage(prevMessage => {
            if (prevMessage && prevMessage !== 'Searching for feed...' && !prevMessage.startsWith('Feed status:'))
              return prevMessage;
            return 'The feed could not be retrieved after 60 seconds. Please try again later.';
          });
        }
      }
    } catch (error) {
      console.error('Error requesting feed:', error);
      setStatusMessage('Error: ' + (error as Error).message);
    } finally {
      setIsSearching(false);
    }
  };

  // Deep-link support: /a/add?url=<feed url> (used by the public /add page once a user
  // logs in/signs up). Pre-fill the search box and run the normal discovery flow once.
  const urlParam = location.searchParams.get('url') ?? '';
  useEffect(() => {
    if (!urlParam || isFeedView) return;
    setSearchInput(urlParam);
    handleSearch(urlParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParam]);

  // ?q=<term> appeared or changed through navigation (a link, back/forward) or is a URL to look up: run that search. When the
  // history goes back to a page without it, the search is closed.
  useEffect(() => {
    if (qParam == searchedTermRef.current || isFeedView) return;
    if (!qParam) return clearSearch();
    setSearchInput(qParam);
    handleSearch(qParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qParam]);

  // The user cleared the search: drop it from the URL.
  const clearSearch = (input = '') => {
    if (searchedTermRef.current && qParam) goTo(routerCtx, location.pathName, 'replace');
    searchedTermRef.current = '';
    setSearchInput(input);
  };

  // Leaving the search for a suggested category or feed: keep the search in the history (back returns
  // to it), but don't strip it from the URL when the input is cleared.
  const leaveSearch = (path: string) => {
    searchedTermRef.current = '';
    setSearchInput('');
    goTo(routerCtx, path);
  };

  // Back targets, for the back links / header crumbs and for Esc (useEscapeBack). Pages entered from
  // Home's Categories section lead back to Home, where the user left it.
  const goHome = () => {
    requestHomeScrollRestore();
    goTo(routerCtx, '/a/home');
  };
  const startOrHome = () => isAddFeedFromHome() ? goHome() : goTo(routerCtx, '/a/add');
  const parentCategory = category?.includes('/') ? category.slice(0, category.lastIndexOf('/')) : undefined;
  useEscapeBack('add-feed-back', searchActive ? undefined
    : feedId ? () => goTo(routerCtx, feedInfo?.category ? `/a/add/${feedInfo.category}` : '/a/add')
    : category ? () => parentCategory ? goTo(routerCtx, `/a/add/${parentCategory}`) : isTopicViaAllTopics() ? goTo(routerCtx, '/a/add/topics') : startOrHome()
    : isTopicsView ? startOrHome
    : listId ? () => goTo(routerCtx, '/a/add')
    : undefined);

  // The paged list of the current view, whose "Load More" pages are restored on return.
  const pagedList = category ? categoryFeeds : listId == 'popular' ? popularFeeds : undefined;
  // The page scrolls in AddFeedPage's .scrollPage container.
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollContainer = () => rootRef.current?.closest<HTMLElement>('.scrollPage');

  // Opening a feed's page (DiscoverTile, FeedTile "View"): its back button returns here (followed
  // feeds included), to the same scroll position with the same number of list pages loaded.
  const setFeedReturnPath = (id: string) => {
    setPreviewReturnPath(location.pathName + location.search);
    setFeedOpenedFromTile(id);
  };
  // The state to return to is saved whenever the page is left, however (feed tile, sidebar, Android
  // back), so the browser history can come back to it as well as the feed's back button.
  const returnPathRef = useRef('');
  returnPathRef.current = location.pathName + location.search;
  const loadedChunksRef = useRef(0);
  loadedChunksRef.current = pagedList?.chunks.length ?? 0;
  useLayoutEffect(() => () =>
    saveAddFeedReturn({ path: returnPathRef.current, scrollTop: scrollContainer()?.scrollTop ?? 0, chunks: loadedChunksRef.current }), []);

  // Returning via a feed's back button: load the list pages again (they come from the query cache),
  // then restore the scroll position. The page grows as its async parts arrive, so keep retrying on
  // re-renders until the position is reached, but give up after a few seconds.
  const [restore] = useState(() => takeAddFeedRestore(location.pathName + location.search));
  const restoreUntilRef = useRef(restore ? Date.now() + 3000 : 0);
  const restoringPages = !!restore && !!pagedList && pagedList.hasMore && pagedList.chunks.length < restore.chunks;
  useEffect(() => {
    if (restoringPages && pagedList.status == QueryStatus.Success && Date.now() < restoreUntilRef.current)
      pagedList.loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoringPages, pagedList?.status, pagedList?.chunks.length]);
  useLayoutEffect(() => {
    const el = scrollContainer();
    if (!el || !restore || restoringPages || Date.now() > restoreUntilRef.current) return;
    el.scrollTop = restore.scrollTop;
    if (Math.abs(el.scrollTop - restore.scrollTop) < 2) restoreUntilRef.current = 0;
  });
  const openFeedPreview = (id: string) => {
    setFeedReturnPath(id);
    requestStartCollapsed();
    goTo(routerCtx, `/a/feed/${id}`);
  };

  const renderFeedTiles = (feeds: (RestUserFeed | RestCatalog)[], hideCategory = false) => feeds.map(feed => (
    <FeedTile
      key={feed.id}
      feed={feed}
      subscribed={isSubscribed(feed.id)}
      isAdmin={isAdmin}
      showPrivacyNote={true}
      hideCategory={hideCategory}
      onAction={() => onFeedAdded(feed.id)}
      onView={() => setFeedReturnPath(feed.id)}
      onQuotaExceeded={() => setShowQuotaDialog(true)}
    />
  ));

  // A paged catalog list (category or "Most popular") as FeedTiles with a "Load More" button.
  const renderFeedList = (list: typeof categoryFeeds, emptyText = 'No feeds found.', hideCategory = false) => (
    <>
      {list.status == QueryStatus.Loading && (
        <div className="addFeedListLoading"><Spinner spinning={true} /></div>
      )}
      {list.status == QueryStatus.Success && !list.chunks.length && <p>{emptyText}</p>}
      {renderFeedTiles(list.chunks.flatMap(chunk => chunk.items), hideCategory)}
      {list.hasMore && list.status == QueryStatus.Success && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
          <TextButton label="Load More" type="major" onClick={() => list.loadMore()} />
        </div>
      )}
    </>
  );

  return (
    <div className="addFeed" ref={rootRef}>
      <FlowForm main={true}>
        <div className="addFeedContainer">
          <div className="addFeedContent">
            {atFeedQuota && (
              <div className="feedQuotaBanner"><FeedQuotaNote /></div>
            )}
            {feedId && (
              <button
                className="addFeedBackLink flowFocussable"
                tabIndex={0}
                onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any}
                onClick={() => {
                  const cat = feedInfo?.category;
                  goTo(routerCtx, cat && formatCategory(cat) ? `/a/add/${cat}` : '/a/add');
                }}
              >
                <ChevronLeftIcon size={18} />
                <span>{(feedInfo?.category && formatCategory(feedInfo.category)?.replace(/\//g, ' / ')) || 'Browse'}</span>
              </button>
            )}
            <FeedSearchBox
              value={searchInput}
              onChange={value => {
                if (!value.trim()) return clearSearch(value);
                setSearchInput(value);
                if (searchActive && !isSearching) {
                  setSearchActive(false);
                  setSearchQuery('');
                  setFeedInfo(null);
                  setStatusMessage('');
                  setSearchedUrl(null);
                }
              }}
              onSubmit={() => handleSearch()}
              onSelectCategory={cat => leaveSearch(`/a/add/${cat}`)}
              onSelectFeed={id => {
                setFeedOpenedFromSearch(id);
                requestStartCollapsed();
                leaveSearch(`/a/feed/${id}`);
              }}
              categories={categories}
              searchActive={searchActive}
              // Not in the native apps: focusing the field on load would pop up the on-screen keyboard.
              autoFocus={autoFocus && !isNativeApp}
              preferFollowed={false}
            />
            {startMode && canUseNewsletters && (
              <button
                type="button"
                className="addFeedNewsletterButton flowFocussable"
                tabIndex={0}
                onKeyDown={buttonKeyHandler}
                onClick={() => goTo(routerCtx, '/a/add/newsletter')}
              >
                <MailIcon size={18} color="currentColor" />
                Get newsletters as a feed
              </button>
            )}
            {statusMessage && !cachedFeed && (
              <div className={`addFeedStatus${isStatusError ? ' error' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {isSearching && <Spinner spinning={true} />}
                <span>
                  {statusMessage === 'rotating' ? (
                    <RotatingMessage messages={loadingMessages} intervalMs={2000} />
                  ) : statusMessage.includes('does not appear to contain an RSS feed') && searchedUrl ? (
                    <>
                      {(() => {
                        try {
                          const url = new URL(searchedUrl);
                          const domain = url.hostname;
                          const homepageUrl = `${url.protocol}//${url.hostname}/`;
                          return (
                            <>
                              This URL does not appear to contain an RSS feed. Please check the URL or try{' '}
                              <a href={homepageUrl} target="_blank" rel="noopener noreferrer">{domain}</a>.
                            </>
                          );
                        } catch {
                          return statusMessage;
                        }
                      })()}
                    </>
                  ) : (
                    statusMessage
                  )}
                </span>
              </div>
            )}
            {cachedFeed && !feedInfo && (
              <div className="addFeedReadyDecision">
                {/* Feed preview card (FeedTile-like) */}
                <div className="readyFeedCard feedTile">
                  <div className="feedTileHeader withImage">
                    {/* The feed isn't created yet (no CDN image), so preview with the generated default icon. */}
                    <img src={getDefaultIconUrl(cachedFeed.shortTitle || cachedFeed.title || '')} alt={cachedFeed.title} className={`feedTileImage${cachedFeed.circularIcon ? ' feedIconCircular' : ''}`} />
                    <div className="feedTileHeaderBody">
                      <div className="feedTileHeaderTop">
                        <div className="feedTileHeaderText">
                          <h3 className="feedTileTitle">{cachedFeed.shortTitle || cachedFeed.title}</h3>
                          {cachedFeed.siteUrl && (
                            <p className="feedTileUrl">{(() => { try { return new URL(cachedFeed.siteUrl).hostname; } catch { return cachedFeed.siteUrl; } })()}</p>
                          )}
                        </div>
                      </div>
                      {cachedFeed.description && (
                        <div className="feedTileDescriptions">
                          <p className="feedTileDescription">{cachedFeed.description.length > 200 ? cachedFeed.description.slice(0, 200) + '…' : cachedFeed.description}</p>
                        </div>
                      )}
                      {cachedFeed.copyright && (
                        <p className="readyFeedCopyright">{/copyright/i.test(cachedFeed.copyright) ? '' : 'Copyright: '}{cachedFeed.copyright}</p>
                      )}
                    </div>
                  </div>
                  {formatCategory(cachedFeed.category) && (
                    <div className="feedTileMetadata">
                      <span className="feedTileMetaItem feedTileMetaCategory">
                        <CategoryIcon category={cachedFeed.category!} size={16} color="var(--gray-400)" />
                        {formatCategory(cachedFeed.category)}
                      </span>
                    </div>
                  )}
                  {cachedFeed.precheckFailure == 'adult' && (
                    <div style={{ padding: '8px 16px', margin: '0 16px', background: '#fff3cd', borderRadius: '6px', fontSize: '0.85rem', color: '#856404', border: '1px solid #ffc107' }}>
                      This feed has been flagged as potentially containing adult content. You can add it as a private feed. Adding it as a public feed requires admin review. Adult content is not allowed.
                    </div>
                  )}
                  {isPrivateOnlyUrl(searchedUrl ?? cachedFeed.url) && (() => {
                    const privateOnlyUrl = searchedUrl ?? cachedFeed.url;
                    const tunnelDomain = getRssTunnelDomain(privateOnlyUrl);
                    // Google News is an expected, well-understood case rather than a problem with the
                    // feed, so it gets a neutral informational note instead of the yellow warning.
                    const isNews = isNewsGoogleUrl(privateOnlyUrl);
                    const noticeStyle = isNews
                      ? { padding: '8px 16px', margin: '0 16px', background: 'var(--dr-bg-panel)', borderRadius: '6px', fontSize: '0.85rem', color: 'var(--gray-300)', border: '1px solid var(--dr-border-panel)' }
                      : { padding: '8px 16px', margin: '0 16px', background: '#fff3cd', borderRadius: '6px', fontSize: '0.85rem', color: '#856404', border: '1px solid #ffc107' };
                    return (
                      <div style={noticeStyle}>
                        {isNews ? (
                          <>
                            <p style={{ margin: '0 0 6px 0' }}>
                              Google News feeds (news.google.com) are personalized topic and search results, not a
                              publisher's own feed, so they are always added as private feeds.
                            </p>
                            <p style={{ margin: 0 }}>
                              Only you can see it, and it counts against your private feed quota.
                            </p>
                          </>
                        ) : tunnelDomain ? (
                          <>
                            <p style={{ margin: '0 0 6px 0' }}>
                              Unfortunately, we can't publish {tunnelDomain} tunnels as public feeds. They repackage another site's content as personalized content, so they can only be added as a private feed.
                            </p>
                            <p style={{ margin: '0 0 6px 0' }}>
                              If the original site offers its own RSS or Atom feed, we recommend adding that directly — without going through {tunnelDomain}.
                            </p>
                            <p style={{ margin: 0 }}>
                              If you run into any issues with an RSS feed on Downread, please get in touch with us.
                            </p>
                          </>
                        ) : (
                          'This feed appears to be personalized (e.g. it uses URL parameters) and can only be added as a private feed.'
                        )}
                      </div>
                    );
                  })()}
                  {/* Action buttons */}
                  <div className="readyFeedActions">
                    {isReadyAction
                      ? <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0' }}><Spinner spinning={true} /><span>Subscribing...</span></div>
                      : <>
                        {!isPrivateOnlyUrl(searchedUrl ?? cachedFeed.url) && (
                        <button className={`readyOption readyOptionPublic flowFocussable${atFeedQuota ? ' readyOptionDisabled' : ''}`} tabIndex={0} disabled={atFeedQuota} onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any} onClick={async () => {
                          if (atFeedQuota) return;
                          setIsReadyAction(true);
                          try {
                            const auth = nonNull(appState.state.authData);
                            const result = await subscribeWithMode(auth, searchedUrl ?? cachedFeed.url, 'public');
                            markFeedAsNew(result.feed.id);
                            appState.feedEngine.handleSubscribeResult(result);
                            setFeedInfo(result.feed);
                            setCachedFeed(null);
                            setStatusMessage('');
                            onFeedAdded(result.feed.id);
                          } catch (e) {
                            if (e instanceof FetchError && e.type == 'AlreadySubscribed')
                              await finishAlreadySubscribed(e.extraData?.feedId);
                            else if (e instanceof FetchError && e.type == 'FeedCreationLimit')
                              setShowCreationLimitDialog(true);
                            else
                              setStatusMessage('Error: ' + (e as Error).message);
                          } finally {
                            setIsReadyAction(false);
                          }
                        }}>
                          <span className="readyOptionTitle">Follow as <b>public</b> feed</span>
                          <span className="readyOptionDesc">{cachedFeed.precheckFailure == 'adult'
                            ? 'Will be submitted for review before becoming visible to other users.'
                            : 'Other users can also read this feed. Most feeds are public.'
                          }</span>
                        </button>
                        )}
                        {(() => {
                          const plan = appState.state.currentUser?.userPlan ?? 'free';
                          const { maxPrivateFeeds } = getPlanLimits(plan);
                          const used = appState.feedEngine.getPrivateFeedCount();
                          const remaining = Math.max(0, maxPrivateFeeds - used);
                          const exhausted = remaining == 0;
                          return <button className={`readyOption readyOptionPrivate flowFocussable${exhausted || atFeedQuota ? ' readyOptionDisabled' : ''}`} tabIndex={0} disabled={exhausted || atFeedQuota} onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any} onClick={async () => {
                            if (exhausted || atFeedQuota) return;
                            setIsReadyAction(true);
                            try {
                              const auth = nonNull(appState.state.authData);
                              const result = await subscribeWithMode(auth, searchedUrl ?? cachedFeed.url, 'private');
                              markFeedAsNew(result.feed.id);
                            appState.feedEngine.handleSubscribeResult(result);
                              setFeedInfo(result.feed);
                              setCachedFeed(null);
                              setStatusMessage('');
                              onFeedAdded(result.feed.id);
                            } catch (e) {
                              if (e instanceof FetchError && e.type == 'AlreadySubscribed')
                                await finishAlreadySubscribed(e.extraData?.feedId);
                              else if (e instanceof FetchError && e.type == 'Quota')
                                setShowQuotaDialog(true);
                              else
                                setStatusMessage('Error: ' + (e as Error).message);
                            } finally {
                              setIsReadyAction(false);
                            }
                          }}>
                            <span className="readyOptionTitle">Follow as <b>private</b> feed</span>
                            <span className="readyOptionDesc">{exhausted
                              ? 'No private feeds left. Upgrade your plan for more.'
                              : <>Only you can see it. {remaining} private feed{remaining != 1 ? 's' : ''} left.</>
                            }</span>
                          </button>;
                        })()}
                        <button className="readyOption readyOptionSkip flowFocussable" tabIndex={0} onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any} onClick={() => {
                          setCachedFeed(null);
                          setStatusMessage('');
                          setSearchActive(false);
                          clearSearch();
                        }}>
                          <span className="readyOptionTitle">Do not follow this feed</span>
                        </button>
                      </>
                    }
                  </div>
                </div>
              </div>
            )}
            {browsing && startMode && (
              <div className="addFeedBrowse">
                <section>
                  <BrowseSectionHeader title="Most popular feeds" seeAll="See all" onSeeAll={() => goTo(routerCtx, '/a/add/list/popular')} />
                  {/* Placeholders keep the row's height reserved while loading, so nothing below shifts. */}
                  <div className="addFeedPopularRow discoverTiles">
                    {popularFeeds.chunks.length
                      ? popularFeeds.chunks[0].items.map(f => <DiscoverTile key={f.id} feed={catalogToHomeFeed(f)} onOpen={() => openFeedPreview(f.id)} />)
                      : popularFeeds.status != QueryStatus.Error && Array.from({ length: 10 }, (_, i) => <DiscoverTilePlaceholder key={i} />)}
                  </div>
                </section>
                {topLevelCategories.length > 0 && (
                  <section>
                    <BrowseSectionHeader title="Browse by topic" seeAll={`See all ${topLevelCategories.length}`} onSeeAll={() => goTo(routerCtx, '/a/add/topics')} />
                    <div className="addFeedTopicGrid" ref={topicGridRef}>
                      {topLevelCategories.slice(0, START_TOPIC_ROWS * topicCols).map(cat => (
                        <CategoryTile key={cat} category={cat} banner onCategoryClick={cat => openTopic(cat, false)} />
                      ))}
                    </div>
                  </section>
                )}
                <section>
                  <BrowseSectionHeader title="Collections" />
                  <div className="addFeedCollectionGrid">
                    {ADD_FEED_LISTS.map(l => <CollectionTile key={l.id} title={l.title} color={l.color} icon={l.icon} onOpen={() => goTo(routerCtx, `/a/add/list/${l.id}`)} />)}
                  </div>
                </section>
              </div>
            )}
            {browsing && isTopicsView && (
              <div className="addFeedCategories addFeedTopicsView">
                <div className="addFeedTopicGrid">
                  {topLevelCategories.map(cat => (
                    <CategoryTile key={cat} category={cat} banner onCategoryClick={cat => openTopic(cat, true)} />
                  ))}
                </div>
              </div>
            )}
            {browsing && listId && (
              <div className="addFeedCategories">
                {listId == 'popular' && renderFeedList(popularFeeds)}
                {onboardingList && (onboarding.data?.[onboardingList]
                  ? <RecentFeedsList feedIds={onboarding.data[onboardingList]!.map(f => f.feedId)} renderFeeds={renderFeedTiles} />
                  // Loaded, but generated before this collection existed.
                  : onboarding.status == QueryStatus.Error || onboarding.data
                    ? <p>The list could not be loaded.</p>
                    : <div className="addFeedListLoading"><Spinner spinning={true} /></div>)}
              </div>
            )}
            {browsing && category && (
              <div className="addFeedCategories">
                {category in CategoryWithSubcategories && (() => {
                  const subcategories = CategoryWithSubcategories[category as keyof typeof CategoryWithSubcategories]
                    .filter(cat => cat != category); // Exclude the selected category itself
                  // Two rows: half the tiles (rounded up) per row; see .addFeedCategoryGrid.subcat.
                  return (
                    <div className="addFeedCategoryGrid subcat" style={{ '--subcat-cols': Math.ceil(subcategories.length / 2) } as React.CSSProperties}>
                      {subcategories.map(cat => <CategoryTile key={cat} category={cat} onCategoryClick={() => goTo(routerCtx, `/a/add/${cat}`)} banner small />)}
                    </div>
                  );
                })()}
                <div className="addFeedCategoryFeedList" style={{ marginTop: '1rem' }}>
                  {renderFeedList(categoryFeeds, 'No feeds found in this category.', true)}
                </div>
              </div>
            )}
            {feedInfo && (feedId || searchActive) && (
              <FeedTile
                feed={feedInfo}
                subscribed={isSubscribed(feedInfo.id)}
                onAction={() => onFeedAdded(feedInfo.id)}
                showPrivacyNote={true}
                isAdmin={isAdmin}
                onQuotaExceeded={() => setShowQuotaDialog(true)}
              >
                <FeedPreview feed={feedInfo} maxPosts={20} />
              </FeedTile>
            )}
            {searchActive && !feedInfo && searchQuery && (
              <div className="addFeedPopularList">
                {searchFeeds.status === QueryStatus.Loading && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <Spinner spinning={true} />
                  </div>
                )}
                {searchFeeds.status === QueryStatus.Success &&
                 searchFeeds.chunks.flatMap(chunk => chunk.items).length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '3rem 2rem',
                    background: 'var(--gray-975)',
                    borderRadius: '8px',
                    margin: '1rem 0'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                    <h3 style={{
                      margin: '0 0 0.5rem 0',
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: 'var(--gray-200)'
                    }}>
                      No feeds found for "{searchQuery}"
                    </h3>
                    <p style={{
                      margin: '0',
                      color: 'var(--gray-400)',
                      fontSize: '0.95rem',
                      lineHeight: 1.5
                    }}>
                      Try searching with different keywords or browse our categories to discover feeds.
                    </p>
                  </div>
                )}
                {searchFeeds.chunks.flatMap(chunk => chunk.items).map((catalogFeed) => (
                  <FeedTile
                    key={catalogFeed.id}
                    feed={catalogFeed}
                    subscribed={isSubscribed(catalogFeed.id)}
                    isAdmin={isAdmin}
                    showPrivacyNote={true}
                    onAction={() => {
                      onFeedAdded(catalogFeed.id);
                    }}
                    onView={() => setFeedReturnPath(catalogFeed.id)}
                    onQuotaExceeded={() => setShowQuotaDialog(true)}
                  />
                ))}
                {searchFeeds.hasMore && searchFeeds.status === QueryStatus.Success && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                    <TextButton
                      label="Load More"
                      type="major"
                      onClick={() => searchFeeds.loadMore()}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Friendly catch-all at the very bottom of the page, below the results. Only
              shown while the user is adding by URL — that's where things go wrong (a feed
              that won't load, a site without a discoverable feed). Adding from the catalog
              just works, so we don't clutter that path with a support address. */}
          {isUrlLike(searchInput) && (
            <div className="addFeedSupportNote">
              <span className="addFeedSupportIcon"><MailIcon size={16} /></span>
              <p className="addFeedSupportText">
                Having issues with a feed? Please write to{' '}
                <a className="addFeedSupportLink flowFocussable" tabIndex={0} href="mailto:support@downread.com"
                  onKeyDown={getKeyHandler(() => { window.location.href = 'mailto:support@downread.com'; })}
                >support@downread.com</a>. A real person reads every mail and we're happy to make it work.
              </p>
            </div>
          )}
        </div>
      </FlowForm>
      <FeedQuotaDialog show={showQuotaDialog} onClose={() => setShowQuotaDialog(false)} />
      <FeedCreationLimitDialog show={showCreationLimitDialog} onClose={() => setShowCreationLimitDialog(false)} />
    </div>
  );
};

export default AddFeed;
